import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import QuarantineNotifications, {
  NotificationData,
} from "../components/QuarantineNotifications";

interface NotificationContextType {
  addNotification: (notification: NotificationData) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  notifications: NotificationData[];
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const addNotification = useCallback((notification: NotificationData) => {
    setNotifications((prev) => {
      // Check if a similar notification already exists to avoid duplicates
      const existingIndex = prev.findIndex(
        (n) =>
          n.fileName === notification.fileName &&
          n.title === notification.title &&
          n.type === notification.type,
      );

      if (existingIndex !== -1) {
        // Replace existing notification
        const updated = [...prev];
        updated[existingIndex] = notification;
        return updated;
      }

      // Add new notification at the beginning
      return [notification, ...prev];
    });
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value: NotificationContextType = {
    addNotification,
    removeNotification,
    clearNotifications,
    notifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <QuarantineNotifications
        notifications={notifications}
        onClose={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};

// Custom hooks for specific notification types
export const useQuarantineNotifications = () => {
  const { addNotification } = useNotifications();

  return {
    notifyFileScanned: (fileName: string, status: string, reason?: string) => {
      // This would typically be called when receiving real-time updates
      // For now, we'll implement basic notifications
      console.log(`File ${fileName} scan result: ${status}`, reason);
      // Future: addNotification could be used here when implementing real-time updates
    },

    notifyAdminAction: (
      action: string,
      fileName: string,
      success: boolean,
      message?: string,
    ) => {
      // This would be called after admin actions complete
      console.log(
        `Admin action ${action} on ${fileName}: ${success ? "success" : "failed"}`,
        message,
      );
      // Future: addNotification could be used here when implementing real-time updates
    },

    // Keep addNotification available for future use
    addNotification,
  };
};
